import { useEffect, type RefObject } from 'react';
import { subscribeScroll } from '../lib/smoothScroll';

const MAX_ANGLE = 5;
const MAX_TILT = 1;
/** Rotation completes when the section is centered / fully in view */
const FULLY_VISIBLE_AT = 0.5;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function smoothstep(value: number) {
  return value * value * (3 - 2 * value);
}

/** Maps section position to 0 (entering) → 0.5 (centered) → 1 (leaving). */
function getSectionProgress(rect: DOMRect, viewportHeight: number) {
  const center = rect.top + rect.height / 2;
  return clamp(1 - center / viewportHeight, 0, 1);
}

export function useSectionDeviceRotation(
  sectionRef: RefObject<HTMLElement | null>,
  mirrored = false
) {
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const applyFront = () => {
      section.style.setProperty('--device-rotate-y', '0deg');
      section.style.setProperty('--device-rotate-x', '0deg');
      section.style.setProperty('--device-scale', '1');
    };

    if (reducedMotion) {
      applyFront();
      return;
    }

    let frame = 0;

    const update = () => {
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress = getSectionProgress(rect, vh);
      const sign = mirrored ? -1 : 1;

      const rotationProgress = clamp(progress / FULLY_VISIBLE_AT, 0, 1);
      const eased = smoothstep(rotationProgress);

      const rotateY = sign * MAX_ANGLE * eased;
      const rotateX = MAX_TILT * eased;
      const scale = 1 - 0.02 * eased;

      section.style.setProperty('--device-rotate-y', `${rotateY.toFixed(2)}deg`);
      section.style.setProperty('--device-rotate-x', `${rotateX.toFixed(2)}deg`);
      section.style.setProperty('--device-scale', scale.toFixed(3));
    };

    const scheduleUpdate = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        update();
        frame = 0;
      });
    };

    update();
    const unsubScroll = subscribeScroll(scheduleUpdate);
    window.addEventListener('resize', scheduleUpdate, { passive: true });

    return () => {
      unsubScroll();
      window.removeEventListener('resize', scheduleUpdate);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [sectionRef, mirrored]);
}
